import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const Home = () => {
  useEffect(() => {
    document.title = 'Home - SOFTORA Technologies';
  }, []);

  const stageRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!stageRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    stageRef.current.style.transform = `perspective(1200px) rotateY(${(x * 12).toFixed(2)}deg) rotateX(${(-y * 10).toFixed(2)}deg)`;
  };

  const handleMouseLeave = () => {
    if (!stageRef.current) return;
    stageRef.current.style.transform = 'perspective(1200px) rotateY(0deg) rotateX(0deg)';
  };
  const highlights = [
    { number: "50+", label: "Completed Projects", desc: "Delivered on-time with clean code and high reliability" },
    { number: "99%", label: "Client Satisfaction", desc: "Trusted by forward-thinking startups & enterprises" },
    { number: "100%", label: "Agile & Transparent", desc: "Regular sprint updates, source code handover & support" },
    { number: "24/7", label: "Technical Support", desc: "Dedicated maintenance, monitoring and scalability" }
  ];

  const featuredServices = [
    {
      title: "Web Engineering",
      subtitle: "React & Next.js Ecosystem",
      desc: "Blazing fast, responsive web applications engineered for optimal SEO, interactive UI/UX, and rock-solid performance.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7L12 12L22 7L12 2Z"/>
          <path d="M2 17L12 22L22 17"/>
          <path d="M2 12L12 17L22 12"/>
        </svg>
      ),
      tags: ["React.js", "Next.js", "TypeScript", "Tailwind"]
    },
    {
      title: "Mobile App Development",
      subtitle: "Cross-Platform with Flutter",
      desc: "Native-grade iOS & Android applications with buttery smooth 60fps animations, offline storage, and modern design systems.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
          <line x1="12" y1="18" x2="12.01" y2="18"/>
        </svg>
      ),
      tags: ["Flutter", "Dart", "iOS", "Android"]
    },
    {
      title: "Cloud & Backend Systems",
      subtitle: "Scalable Infrastructure",
      desc: "Robust backend architectures with Firebase, Node.js, and cloud functions capable of handling millions of requests effortlessly.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
        </svg>
      ),
      tags: ["Firebase", "Node.js", "MySQL", "PostgreSQL", "REST APIs"]
    },
    {
      title: "AI & Smart Automation",
      subtitle: "Intelligent Workflows",
      desc: "Empower your software with automated workflows, AI integrations, chatbot agents, and intelligent data processing.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
      ),
      tags: ["AI Tools", "NLP", "Automation", "Machine Learning"]
    }
  ];

  const pillars = [
    {
      step: "01",
      title: "Future-Ready Architecture",
      desc: "We build scalable, modular codebases that grow seamlessly alongside your user base and business requirements."
    },
    {
      step: "02",
      title: "Risk-Free Agile Engagement",
      desc: "Milestone-based delivery, clear documentation, regular interactive demos, and zero vendor lock-in."
    },
    {
      step: "03",
      title: "Cutting-Edge Tech Stack",
      desc: "From React and Flutter to modern cloud solutions, we employ the most battle-tested and efficient developer tools."
    },
    {
      step: "04",
      title: "End-to-End Ownership",
      desc: "From initial UI/UX blueprints to deployment, launch support, and post-release maintenance, we handle it all."
    }
  ];

  return (
    <div className="home-page">
      
      {/* Hero Section */}
      <section className="home-hero">
        <div className="hero-glow-blob-1"></div>
        <div className="hero-glow-blob-2"></div>

        <div className="home-hero-container">
          <div className="home-hero-content">
            
            <div className="hero-badge">
              <span className="hero-badge-dot"></span>
              <span>INNOVATIVE SOFTWARE & IT</span>
            </div>

            <h1 className="home-hero-title">
              Accelerate Your Business With <span className="title-gradient">Next-Gen Software</span>
            </h1>

            <p className="home-hero-description">
              Softora is a rising tech-based company delivering modern IT project solutions, documentation, and software development services.
            </p>

            <div className="home-hero-actions">
              <Link to="/services" className="btn-primary">
                <span>EXPLORE SERVICES</span>
                <span className="btn-arrow">→</span>
              </Link>
              <Link to="/contact" className="btn-secondary">
                <span>BOOK A CONSULTATION</span>
              </Link>
            </div>

            {/* Quick Tech Badges */}
            {/*<div className="hero-tech-strip">
              <span className="tech-strip-label">CORE STACK:</span>
              <div className="tech-strip-items">
                <span className="tech-pill">React</span>
                <span className="tech-pill">Flutter</span>
                <span className="tech-pill">Firebase</span>
                <span className="tech-pill">Node.js</span>
                <span className="tech-pill">Python</span>
                <span className="tech-pill">Next.js</span>
              </div>
            </div>*/}

          </div>

          <div 
            className="home-hero-graphic"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <div className="hero-character-wrap">
              {/* Soft futuristic ambient aura */}
              <div className="hero-character-aura"></div>
              
              {/* 3D Animated Hero Image Stage */}
              <div 
                ref={stageRef}
                className="hero-character-stage"
              >
                <img
                  src="/hero-turning.webp"
                  alt="Softora AI & Next-Gen Engineering"
                  className="hero-character-img"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Strip */}
      <section className="metrics-section">
        <div className="metrics-container">
          {highlights.map((item, index) => (
            <div key={index} className="metric-box">
              <div className="metric-number">{item.number}</div>
              <div className="metric-label">{item.label}</div>
              <div className="metric-desc">{item.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Services Section */}
      <section className="featured-services-section">
        <div className="section-container">
          <div className="section-header text-center">
            <span className="section-eyebrow">WHAT WE EXCEL AT</span>
            <h2 className="section-title">
              Engineered for Speed, Scalability, and <span className="title-gradient">Impact</span>
            </h2>
            <p className="section-subtitle">
              We offer comprehensive digital product development tailored to meet modern market standards.
            </p>
          </div>

          <div className="services-grid">
            {featuredServices.map((service, index) => (
              <div key={index} className="service-feature-card">
                <div className="service-card-icon-wrap">
                  {service.icon}
                </div>
                <div className="service-card-subtitle">{service.subtitle}</div>
                <h3 className="service-card-title">{service.title}</h3>
                <p className="service-card-desc">{service.desc}</p>
                <div className="service-card-tags">
                  {service.tags.map((tag, tIndex) => (
                    <span key={tIndex} className="service-tag">{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="section-cta-wrap text-center">
            <Link to="/services" className="view-more-link">
              <span>View All 8+ Dedicated Services</span>
              <span className="arrow-icon">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Softora */}
      <section className="why-softora-section">
        <div className="section-container">
          <div className="why-softora-layout">
            <div className="why-left-content">
              <span className="section-eyebrow">THE SOFTORA ADVANTAGE</span>
              <h2 className="section-title">
                Why Visionary Businesses Choose <span className="title-gradient">Softora</span>
              </h2>
              <p className="section-text">
                We believe that modern technology shouldn’t be a bottleneck for growth. We take pride in craftsmanship, maintain crystal-clear communication, and develop solutions that stand the test of time.
              </p>
              
              <div className="why-check-list">
                <div className="check-item">
                  <span className="check-icon">✓</span>
                  <span>Direct collaboration with senior engineers</span>
                </div>
                <div className="check-item">
                  <span className="check-icon">✓</span>
                  <span>Full Intellectual Property (IP) & code ownership</span>
                </div>
                <div className="check-item">
                  <span className="check-icon">✓</span>
                  <span>Modern UI/UX with micro-interactions & speed</span>
                </div>
                <div className="check-item">
                  <span className="check-icon">✓</span>
                  <span>Post-launch warranty & comprehensive documentation</span>
                </div>
              </div>

              <Link to="/about" className="btn-outline">
                <span>Learn More About Our Team →</span>
              </Link>
            </div>

            <div className="why-pillars-grid">
              {pillars.map((pillar, index) => (
                <div key={index} className="pillar-card">
                  <div className="pillar-step">{pillar.step}</div>
                  <h4 className="pillar-title">{pillar.title}</h4>
                  <p className="pillar-desc">{pillar.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Ribbon */}
      <section className="home-tech-banner">
        <div className="section-container">
          <div className="tech-banner-card">
            <div className="tech-banner-text">
              <span className="section-eyebrow">TECH MASTERY</span>
              <h3>Explore Our Complete Engineering Stack</h3>
              <p>Discover the frontend, mobile, cloud, database, and automation tools we use to build high-scale solutions.</p>
            </div>
            <Link to="/technologies" className="btn-primary">
              <span>EXPLORE TECH STACK →</span>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
